import { useCallback, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { usePrefersReducedMotion } from '../../lib/motion';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { formatAzimuth, MATERIAL_SPECS } from './scene-model';
import type { SceneReadout } from './scene';
import { Steve3dViewport } from './Steve3dViewport';
import styles from './threed.module.css';

/** Une ligne du relevé : clé, pointillés, valeur. */
function Row({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className={styles.row}>
      <span className={styles.rowKey}>{label}</span>
      <span className={styles.rowFill} aria-hidden="true" />
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

/**
 * Skin 9 — 3D : Steve n'est plus un dessin, c'est un volume.
 *
 * Le parti pris de l'habillage : **tout est en perspective**. Sol qui fuit,
 * titre extrudé en quatorze ombres, panneaux inclinés à tranche visible, gizmo
 * d'axes piloté par la caméra réelle. Une version à plat rendrait ce skin
 * interchangeable avec le skin saas.
 *
 * Le contenu, lui, est celui des huit autres : mêmes quinze pads, même livre
 * d'or, mêmes crédits — seul le vocabulaire visuel change.
 */
export function ThreedSkin(): ReactNode {
  const { lang, seen, draft } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const questions = faq(lang);
  const reducedMotion = usePrefersReducedMotion();

  /** Publié une fois par seconde par la scène, jamais par image. */
  const [readout, setReadout] = useState<SceneReadout | null>(null);
  const onReadout = useCallback((next: SceneReadout) => {
    setReadout(next);
  }, []);

  const triangles = readout === null ? null : readout.triangles.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB');

  return (
    <div className={cx(styles.root, reducedMotion && styles.still)}>
      <div className={styles.backdrop} aria-hidden="true">
        <div className={styles.floor} />
      </div>
      <div className={styles.vignette} aria-hidden="true" />

      <header className={styles.topbar}>
        <span className={styles.topbarLeft}>
          <span className={styles.topbarMark} aria-hidden="true" />
          {t(S.threed.chrome.workspace, lang)}
        </span>
        <span className={styles.topbarRight} role="status">
          <span>
            {readout === null
              ? t(S.threed.chrome.axread, lang, { az: '···', el: '··' })
              : t(S.threed.chrome.axread, lang, {
                  az: formatAzimuth(readout.orientation.azimuth),
                  el: String(Math.round(readout.orientation.elevation)),
                })}
          </span>
          <span>
            {triangles === null
              ? t(S.threed.chrome.poly_loading, lang)
              : t(S.threed.chrome.poly, lang, { n: triangles })}
          </span>
          <span className={styles.topbarValue}>{mixer.stateLabel}</span>
        </span>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroTitleBlock}>
          <p className={styles.kicker}>{t(S.threed.hero.kicker, lang)}</p>
          <h1 className={styles.title}>{t(S.threed.hero.title, lang)}</h1>
        </div>
        <div className={styles.heroSide}>
          <div>{t(S.threed.hero.scale, lang)}</div>
          <div>{t(S.threed.hero.objects, lang, { n: readout?.objects ?? '—' })}</div>
          <div className={styles.heroSideValue}>
            {readout === null ? '—' : t(S.threed.chrome.fps, lang, { n: readout.fps })}
          </div>
        </div>
      </section>

      <Steve3dViewport onReadout={onReadout} />

      <div className={styles.grid}>
        <section className={styles.panel}>
          <span className={styles.panelTag}>{t(S.threed.sections.s01, lang)}</span>
          <div className={styles.rows}>
            <Row label={t(S.threed.survey.name, lang)} value="steve" />
            <Row label={t(S.threed.survey.type_k, lang)} value={t(S.threed.survey.type_v, lang)} />
            <Row
              label={t(S.threed.survey.objects, lang)}
              value={readout === null ? '—' : String(readout.objects)}
            />
            <Row
              label={t(S.threed.survey.tris, lang)}
              value={triangles ?? t(S.threed.chrome.poly_loading, lang)}
            />
            <Row label={t(S.threed.survey.height_k, lang)} value={t(S.threed.survey.height_v, lang)} />
            <Row label={t(S.threed.survey.legs, lang)} value="2" />
            <Row label={t(S.threed.survey.arms, lang)} value="2" />
            <Row label={t(S.threed.survey.dorsal_k, lang)} value={t(S.threed.survey.dorsal_v, lang)} />
            <Row label={t(S.threed.survey.odour_k, lang)} value={t(S.threed.survey.odour_v, lang)} />
          </div>

          <p className={styles.subhead}>{t(S.threed.survey.materials, lang)}</p>
          <div className={styles.swatches}>
            {[
              [MATERIAL_SPECS.matte.body.color, t(S.threed.survey.mat_body, lang)],
              [MATERIAL_SPECS.matte.dark.color, t(S.threed.survey.mat_dark, lang)],
              ['#ffffff', t(S.threed.survey.mat_eye, lang)],
              ['#10131a', t(S.threed.survey.mat_pupil, lang)],
            ].map(([color, label]) => (
              <div key={label} className={styles.swatchRow}>
                <span className={styles.swatch} style={{ background: color }} aria-hidden="true" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className={cx(styles.panel, styles.panelRight)}>
          <span className={styles.panelTag}>{t(S.threed.sections.s02, lang)}</span>
          <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
          <p className={cx(styles.paragraph, styles.paragraphDim)}>
            <AuthorText linkClassName={styles.creditLink}>
              {t(S.threed.observations.p2, lang)}
            </AuthorText>
          </p>
          <div className={styles.stats}>
            <div>
              <span className={styles.statValue}>{seen}</span>
              <span className={styles.statLabel}>{t(S.threed.observations.renders, lang)}</span>
            </div>
            <div>
              <span className={styles.statValue}>2</span>
              <span className={styles.statLabel}>{t(S.threed.observations.legs, lang)}</span>
            </div>
            <div>
              <span className={styles.statValue}>100</span>
              <span className={styles.statLabel}>{t(S.threed.observations.orange, lang)}</span>
            </div>
          </div>
        </section>

        <section className={styles.panel}>
          <span className={styles.panelTag}>{t(S.threed.sections.s03, lang)}</span>
          <VuMeter className={styles.vu} barClassName={styles.vuBar} />

          <div className={styles.pads}>
            {mixer.pads.map((pad) => (
              <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                <span>{pad.label}</span>
                <span className={styles.padTime}>{pad.timeLabel}</span>
              </button>
            ))}
          </div>

          <div className={styles.buttons}>
            <button type="button" className={styles.button} onClick={actions.toggleAudio}>
              {audio.label}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
              {mixer.holdLabel}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onStop}>
              {t(S.threed.mixer.stop, lang)}
            </button>
          </div>

          {mixer.sliders.map((slider) => (
            <div key={slider.key} className={styles.slider}>
              <label className={styles.sliderLabel} htmlFor={`threed-${slider.key}`}>
                {slider.label}
              </label>
              <input
                id={`threed-${slider.key}`}
                type="range"
                className={styles.sliderInput}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={slider.value}
                onChange={(event) => {
                  slider.onChange(event.currentTarget.valueAsNumber);
                }}
              />
              <output className={styles.sliderValue} htmlFor={`threed-${slider.key}`}>
                {slider.valueLabel}
              </output>
            </div>
          ))}

          <div className={styles.buttons}>
            {mixer.presets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                className={styles.button}
                onClick={preset.onSelect}
              >
                {preset.label}
              </button>
            ))}
            <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
              {mixer.wobbleLabel}
            </button>
          </div>

          <p className={styles.note} role="status">
            {t(S.threed.mixer.note, lang, { audioHint: audio.hint })}
          </p>
        </section>

        <section className={cx(styles.panel, styles.panelRight)}>
          <span className={styles.panelTag}>{t(S.threed.sections.s04, lang)}</span>
          <ul className={styles.entries}>
            {guestbook.map((entry) => (
              <li key={entry.id} className={styles.entry}>
                <span className={styles.entryWho}>{entry.who}</span>
                <span className={styles.entryText}>{entry.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.compose}>
            <input
              type="text"
              className={styles.input}
              value={draft}
              placeholder={t(S.threed.signoffs.placeholder, lang)}
              aria-label={t(S.guestbook.placeholder, lang)}
              onChange={(event) => {
                actions.setDraft(event.currentTarget.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') actions.postEntry();
              }}
            />
            <button type="button" className={styles.button} onClick={actions.postEntry}>
              {t(S.threed.signoffs.send, lang)}
            </button>
          </div>
        </section>
      </div>

      {/* La FAQ tient dans le relevé des observations : pas de panneau à part,
          la grille est déjà à quatre. */}
      <div className={styles.grid}>
        <section className={cx(styles.panel, styles.panelRight, styles.panelWide)}>
          <span className={styles.panelTag}>{t(S.threed.sections.faq, lang)}</span>
          <dl className={styles.rows}>
            {questions.map((item) => (
              <div key={item.question}>
                <dt className={styles.subhead}>{item.question}</dt>
                <dd className={cx(styles.paragraph, styles.paragraphDim)}>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <footer className={styles.cartouche}>
        <div className={styles.case}>
          <span className={styles.caseKey}>{t(S.threed.cartouche.drawing_k, lang)}</span>
          <span className={styles.caseValue}>
            <Credits linkClassName={styles.creditLink} />
          </span>
        </div>
        <div className={styles.case}>
          <span className={styles.caseKey}>{t(S.threed.cartouche.status_k, lang)}</span>
          <span className={styles.caseValue}>{t(S.threed.cartouche.status_v, lang)}</span>
        </div>
        <div className={styles.case}>
          <span className={styles.caseKey}>{t(S.threed.survey.name, lang)}</span>
          <span className={styles.caseValue}>steve.glb</span>
        </div>
        <div className={styles.case}>
          <span className={styles.caseKey}>{t(S.threed.cartouche.hint_k, lang)}</span>
          <span className={cx(styles.caseValue, styles.accent)}>{t(S.common.egg_hint, lang)}</span>
        </div>
      </footer>
    </div>
  );
}
